import React from 'react'
import {
  ProductCourseCollection as CourseCollectionCardComponent,
  getImgProps,
  Price,
} from '@superhi/design'
import { useQuery } from '@apollo/client'

import { API } from '../../api'
import { DURATION } from '../CourseChip'
import { useBasket } from '../../basket'
import { buildPath } from '../../routing'

import QUERY from './queries'

type ComponentProps = Omit<
  React.ComponentProps<typeof CourseCollectionCardComponent>,
  'price' | 'fullPrice' | 'courses' | 'buttonOnClick' | 'buttonText' | 'buttonIsDisabled' | 'image'
>

type Props = ComponentProps &
  API.CourseCollectionCardComponent & {
    imageLazy?: boolean
  }

const courseOrders = [
  'digital-project-management',
  'designing-workshops-that-stick',
  'presenting-persuasively',
  'the-responsible-technology-toolkit',
  'better-than-launched',
  'visual-design-and-branding',
  'learn-ux-ui-from-game-design',
  'intro-to-user-interface-design',
  'introduction-to-user-experience-design',
  'how-to-make-instagram-filters-with-spark-ar',
  'plan-design-and-code-your-first-website',
  'html-css-javascript-foundation',
  'generative-art-with-touchdesigner',
  'javascript-for-designers',
  'chatgpt-workshop',
  'creative-coding',
  'intro-to-python',
  'plan-design-code-your-first-app',
  'math-for-creatives',
  'page-transitions',
  'animation-for-the-web-svg-css-and-javascript',
  'experimental-javascript',
  'experimental-typography-for-the-web',
  'crypto-and-web3-for-creatives',
  'intro-to-react-javascript',
  'advanced-css-and-javascript',
  'data-visualization-with-d3',
  'shaders-for-the-web',
  'ajax-and-apis',
  'command-line-for-creatives',
  'intro-to-full-stack-js',
  'shopify-theme-development',
  'building-shopify-stores',
  'wordpress',
  'ruby-on-rails',
  'intro-to-3d-modeling-workshop',
]

const CourseCollectionCard: React.FC<Props> = ({
  id,
  variants,
  slug,
  fullPrice,
  price,
  image,
  imageLazy = false,
  ...props
}) => {
  const { data } = useQuery<
    API.CourseCollectionCardComponent_price,
    API.CourseCollectionCardComponent_priceVariables
  >(QUERY, {
    variables: {
      id,
    },
    ssr: false,
  })
  const basket = useBasket()
  const [addLineItems, addLineItemsResponse] = basket.addLineItems
  const imageProps = React.useMemo(
    () => ({
      ...getImgProps(
        image.url,
        {
          small: 190,
          medium: 50,
          large: 40,
          xlarge: 40,
        },
        {
          fm: 'png8',
        },
      ),
      lazy: imageLazy,
    }),
    [image.url, imageLazy],
  )

  const courseVariants = React.useMemo(
    () =>
      variants?.nodes
        .filter((variant) => variant.product.__typename === 'ProductCourse')
        .map((variant) => {
          const variantProductCourse =
            variant.product as API.CourseCollectionCardComponent_variants_nodes_product_ProductCourse

          return {
            id: variant.id,
            href: buildPath('courses-view', {
              params: { slug: variantProductCourse.slug },
            }),
            title: variantProductCourse.title,
            image: {
              ...getImgProps(variantProductCourse.image.url, {
                small: 10,
                medium: 10,
                large: 15,
                xlarge: 15,
              }),
              lazy: imageLazy,
            },
            duration: DURATION[variantProductCourse.length],
            difficultyLevel: variantProductCourse.difficultyLevel,
          }
        })
        .sort((variantA, variantB) => {
          const indexA = courseOrders.findIndex((slug) => variantA.href.includes(slug))
          const indexB = courseOrders.findIndex((slug) => variantB.href.includes(slug))
          return indexA - indexB
        }) || [],
    [variants, imageLazy],
  )

  const handleClickButton = React.useCallback(async () => {
    try {
      await addLineItems(courseVariants.map((variant) => variant.id).map((id) => id))
    } catch (e) {
      console.log(e)
    }
  }, [courseVariants, addLineItems])

  const isInBasket = React.useMemo(
    () => courseVariants.every((variant) => basket.containsVariant(variant.id)),
    [courseVariants, basket],
  )

  const fixedPrice = data?.productCollection?.price || price
  const fixedFullPrice = data?.productCollection?.fullPrice || fullPrice

  return (
    <CourseCollectionCardComponent
      {...props}
      image={imageProps}
      price={Price.format(fixedPrice.value, fixedPrice.currencyCode)}
      fullPrice={Price.format(fixedFullPrice.value, fixedFullPrice.currencyCode)}
      courses={courseVariants}
      buttonOnClick={handleClickButton}
      buttonText={
        addLineItemsResponse.loading
          ? 'Adding...'
          : isInBasket
          ? "It's in your cart!"
          : 'Add to cart'
      }
      buttonIsDisabled={addLineItemsResponse.loading || isInBasket}
    />
  )
}

export { default as FRAGMENT } from './fragments'
export default CourseCollectionCard
